import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input, Switch } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { getSignalState, setSignalConfig, saveSignalValueTable, approveSignalValueTable, flushSignal, getSignalConversionActions, type AdsConversionAction, type SignalTiers } from '@/data/client';


const SIGNAL_TIER_KEYS = ['5', '4', '3', '2', '1'] as const;

// Signal Return per-client controls (plan E2-3 + E1-7): switchboard, value
// table (versioned; large edits need a SECOND admin), ledger snapshot. Nothing
// is sent anywhere until enabled here AND the consent gate is satisfied — and
// mode 'live' is not offered until the E5 pilot.
export function SignalReturnSection({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['signal', orgId], queryFn: () => getSignalState(orgId) });
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('off');
  const [agreement, setAgreement] = useState(false);
  const [actionId, setActionId] = useState('');
  const [tier5ActionId, setTier5ActionId] = useState('');
  const [rosterTier, setRosterTier] = useState('');
  const [tiers, setTiers] = useState<Record<string, string>>({ '1': '0', '2': '', '3': '', '4': '', '5': '' });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [inited, setInited] = useState(false);
  const [adsActions, setAdsActions] = useState<AdsConversionAction[] | null>(null);

  const active = data?.valueTables.find((t) => t.status === 'active');
  const pending = data?.valueTables.find((t) => t.status === 'pending_approval');

  useEffect(() => {
    if (!data || inited) return;
    const c = data.config;
    if (c) {
      setEnabled(c.enabled); setMode(c.mode); setAgreement(c.agreementUpdated);
      setActionId(c.conversionActionId); setTier5ActionId(c.tier5ActionId); setRosterTier(c.rosterTier);
    }
    const t = data.valueTables.find((v) => v.status === 'active')?.tiers;
    if (t) setTiers({ '1': String(t['1']), '2': String(t['2']), '3': String(t['3']), '4': String(t['4']), '5': String(t['5']) });
    setInited(true);
  }, [data, inited]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['signal', orgId] });
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key); setMsg('');
    try { await fn(); refresh(); }
    catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(''); }
  };

  const loadActions = () => run('actions', async () => {
    const r = await getSignalConversionActions(orgId);
    setAdsActions(r.actions ?? []);
    if (!r.actions?.length) setMsg('No enabled conversion actions found in this Ads account.');
  });
  const saveConfig = () => run('config', () => setSignalConfig(orgId, {
    enabled, mode: mode as 'off' | 'validate_only' | 'report_only' | 'live', agreementUpdated: agreement,
    conversionActionId: actionId, tier5ActionId, rosterTier,
  }));
  const saveTable = () => run('table', async () => {
    const t = Object.fromEntries(Object.entries(tiers).map(([k, v]) => [k, Number(v)])) as unknown as SignalTiers;
    const r = await saveSignalValueTable(orgId, { tiers: t, note });
    setMsg(r.status === 'pending_approval'
      ? `Saved as v${r.version} — large change: a SECOND admin must approve it before it takes effect.`
      : `Value table v${r.version} is now active.`);
  });

  const counts = data?.counts ?? {};
  const countLine = ['pending', 'validated', 'sent', 'failed'].filter((k) => counts[k]).map((k) => `${counts[k]} ${k}`).join(' · ');

  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">Signal Return (Google Ads feedback)</h4>
      <p className="dash-muted" style={{ margin: '0 0 10px' }}>
        Pushes this client&rsquo;s AI lead grades into their Google Ads account as value-bearing conversions (Data Manager API).
        Without the signed agreement, every send stays a <strong>validate-only dry run</strong> — enforced in code.
      </p>

      <div className="lead-modal__grid">
        <Switch label="Enable Signal Return" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <label className="tr-field"><span className="tr-field__label">Mode</span>
          <select className="tr-select" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="off">Off</option>
            <option value="validate_only">Validate only (dry run)</option>
            <option value="report_only">Report only (real sends, no bidding)</option>
          </select>
        </label>
        <div>
          <Input label="Conversion action ID (tQ Graded Lead)" value={actionId} onChange={(e) => setActionId(e.target.value)} placeholder="from Google Ads" />
          <button type="button" className="admin-linkbtn" style={{ marginTop: 4 }} onClick={loadActions} disabled={busy === 'actions'}>
            {busy === 'actions' ? 'Loading from Google Ads…' : 'Load conversion actions from Google Ads'}
          </button>
          {adsActions && adsActions.length > 0 && (
            <div className="miner-list" style={{ marginTop: 6 }}>
              {adsActions.map((a) => (
                <div key={a.id} className="miner-src" style={{ cursor: 'default' }}>
                  <span><strong>{a.name}</strong> · {a.id}{a.primary ? ' · PRIMARY' : ''}</span>
                  <Button size="sm" variant="outline" onClick={() => { setActionId(a.id); setMsg(`Selected ${a.name} (${a.id}) — remember to Save.`); }}>Use</Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <Input label="Tier-5 action ID (optional)" value={tier5ActionId} onChange={(e) => setTier5ActionId(e.target.value)} />
        <label className="tr-field"><span className="tr-field__label">Roster tier</span>
          <select className="tr-select" value={rosterTier} onChange={(e) => setRosterTier(e.target.value)}>
            <option value="">—</option><option value="A">A (≥50 QL/mo)</option><option value="B">B (15–50)</option><option value="C">C (report-only)</option>
          </select>
        </label>
        <Switch label="Client agreement updated (consent gate)" checked={agreement} onChange={(e) => setAgreement(e.target.checked)} />
      </div>
      <div className="lw-actions">
        <Button variant="primary" size="sm" disabled={busy === 'config'} onClick={saveConfig}>{busy === 'config' ? 'Saving…' : 'Save Signal settings'}</Button>
      </div>

      <div style={{ marginTop: 14 }}>
        <strong style={{ fontSize: 'var(--text-sm)' }}>Value table {active ? `(v${active.version} active)` : '(none yet — required before anything can send)'}</strong>
        {data?.config?.experimentActive && <p className="err">Edits frozen: this account is in an active experiment.</p>}
        <div className="signal-tiers">
          {SIGNAL_TIER_KEYS.map((k) => (
            <label key={k} className="tr-field">
              <span className="tr-field__label">Tier {k} $</span>
              <input className="tr-select" inputMode="decimal" value={tiers[k]} onChange={(e) => setTiers((t) => ({ ...t, [k]: e.target.value }))} />
            </label>
          ))}
        </div>
        <Input label="Change note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="why this table (e.g. close 30% × avg $8k × margin 40%)" />
        <div className="lw-actions">
          <Button variant="outline" size="sm" disabled={busy === 'table' || !!data?.config?.experimentActive} onClick={saveTable}>{busy === 'table' ? 'Saving…' : 'Save value table'}</Button>
        </div>
        {pending && (
          <div className="gap-list" style={{ marginTop: 8 }}>
            <div className="gap-list__head"><Icon name="alert-circle" size={12} /> v{pending.version} awaits a second admin&rsquo;s approval{pending.note ? ` — ${pending.note}` : ''}</div>
            <div className="lw-actions">
              <Button size="sm" variant="primary" disabled={busy === 'approve'} onClick={() => run('approve', () => approveSignalValueTable(orgId, pending.id))}>Approve v{pending.version}</Button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Badge variant={counts.failed ? 'warning' : 'neutral'}>{countLine || 'no events yet'}</Badge>
        <Button size="sm" variant="ghost" disabled={busy === 'flush'} onClick={() => run('flush', () => flushSignal(orgId))}>{busy === 'flush' ? 'Flushing…' : 'Reconcile & flush now'}</Button>
      </div>
      {msg && <p className="dash-muted" style={{ marginTop: 8 }}>{msg}</p>}
      {(data?.events?.length ?? 0) > 0 && (
        <div style={{ marginTop: 8, maxHeight: 180, overflow: 'auto', fontSize: 'var(--text-xs)' }}>
          {data!.events.slice(0, 10).map((e) => (
            <div key={e.tq_lead_id} style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{e.tq_lead_id.slice(0, 16)}…</span>
              <span>tier {e.score != null ? Math.round(e.score) : '—'}</span>
              <span>{e.value != null ? `$${e.value}` : '—'}</span>
              <Badge variant={e.status === 'failed' ? 'warning' : e.status === 'sent' ? 'success' : 'neutral'}>{e.status}</Badge>
              {e.last_error && <span className="dash-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.last_error}</span>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
